import { fmtDisplayDate } from '@utils/displayDate'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'

/**
 * Spec 190 (CC-54303): Kucker Marino's Exago Aged AR report → the universal grouped grid
 * contract ({ columns, reportData, reportFooterCells }).
 *
 * cb-api has already reconciled every figure against Exago's own totals to the cent and
 * refuses to answer when they disagree. So the group subtotals and the grand total here
 * are EXAGO'S values, placed verbatim — nothing is summed on this side. The grouped grid
 * then renders them as server totals, exactly as it does for CB's 86 grouped reports.
 *
 * A plain module (no React) so the page file stays component-only.
 */

export const EARLIEST_DEFAULT = '1900-01-01'

// Exago's own column headers, in Exago's order. `Client #` splits Exago's "Client" band
// (number + name) into two sortable columns.
export const COLUMNS = Object.freeze([
  { key: 'clientNumber', label: 'Client #', kind: 'text' },
  { key: 'clientName', label: 'Client', kind: 'text' },
  { key: 'balance', label: 'Balance Due', kind: 'money', totaled: true },
  { key: 'b0_30', label: '0-30', kind: 'money', totaled: true },
  { key: 'b31_59', label: '31-59', kind: 'money', totaled: true },
  { key: 'b60_89', label: '60-89', kind: 'money', totaled: true },
  { key: 'b90_119', label: '90-119', kind: 'money', totaled: true },
  { key: 'b120plus', label: '>120', kind: 'money', totaled: true },
  { key: 'lastPaymentDate', label: 'Date of Last Payment', kind: 'date' },
  // Exago prints no total for these two, so neither do we.
  { key: 'paidLast30', label: 'Amount Paid in Last 30 Days', kind: 'money' },
  { key: 'paidYtd', label: 'Amount Paid YTD', kind: 'money' },
  { key: 'arNote', label: 'AR Notes', kind: 'text' },
])

export const PERCENT_KEYS = Object.freeze(['b0_30', 'b31_59', 'b60_89', 'b90_119', 'b120plus'])

// displayValue / groupHeaderContent are injected as HTML by the universal grids
// (dangerouslySetInnerHTML), so every value from Exago is escaped. Client names and
// notes are free text typed by the firm.
export const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')

/** `$1,234.56` / `-$50.50`; `$0.00` stays visible, as Exago prints it. Null → blank. */
export const fmtMoney = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return ''
  const n = Number(v)
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${n < 0 ? '-' : ''}$${abs}`
}

const cellFor = (col, columnId, client) => {
  const v = client[col.key]
  if (col.kind === 'money') {
    // dataValue drives numeric sort and the grid's SUM-only filtered totals.
    return v == null ? { columnId, displayValue: '' } : { columnId, displayValue: fmtMoney(v), dataValue: Number(v) }
  }
  if (col.kind === 'date') {
    if (!v) return { columnId, displayValue: '' }
    const t = Date.parse(`${v}T00:00:00`)
    return { columnId, displayValue: esc(fmtDisplayDate(v)), ...(Number.isFinite(t) ? { dataValue: t } : {}) }
  }
  return { columnId, displayValue: esc(v ?? '') }
}

const totalsCells = (totals, label) => COLUMNS.map((col, i) => {
  if (i === 0) return { columnId: i, displayValue: esc(label) }
  if (col.totaled) return { columnId: i, displayValue: fmtMoney(totals?.[col.key]) }
  return { columnId: i, displayValue: '' }
})

/*
 * NOT a grid footer row, deliberately. Exago's "% of total" line sits under money
 * columns, and the export writers type a numeric column's cells with `toNumber`, which
 * strips `%`: "98.74%" would land in Excel as 98.74 in a currency column. The screen
 * shows the percentages in their own small table (the page) and the documents state them
 * as TEXT in the criteria line (`percentSummary` below), where nothing re-parses them.
 */

/** The grouped grid's report object, or null before a run. */
export const buildGridReport = (data) => {
  if (!data) return null
  const columns = COLUMNS.map((col, i) => ({
    columnId: i,
    aliasKey: col.key,
    label: col.label,
    visible: true,
    columnAlignment: col.kind === 'money' ? 'RIGHT' : 'LEFT',
    // Clients in Exago's own order until the user sorts.
  }))
  const reportData = data.attorneys.map((a) => ({
    isGroup: true,
    groupHeaderContent: `${esc(a.name)} <span class="text-xs font-normal text-gray-500">(${a.clients.length})</span>`,
    dataRows: a.clients.map((client) => ({ isGroup: false, dataCells: COLUMNS.map((col, i) => cellFor(col, i, client)) })),
    groupFooterCells: totalsCells(a.totals, 'Total'),
  }))
  return {
    columns,
    reportData,
    reportFooterCells: data.attorneys.length ? totalsCells(data.grandTotal, 'Grand Total') : [],
    recordCount: data.counts?.clients ?? data.attorneys.reduce((n, a) => n + a.clients.length, 0),
  }
}

const LABEL = Object.fromEntries(COLUMNS.map((c) => [c.key, c.label]))

/** Exago's "% of total" line as one sentence of text, or null when Exago printed none. */
export const percentSummary = (percents) => {
  const parts = PERCENT_KEYS.filter((k) => percents?.[k]).map((k) => `${LABEL[k]}: ${percents[k]}`)
  return parts.length ? `Share of Balance Due — ${parts.join(', ')}` : null
}

/**
 * What the Advanced Report itself leaves out or measures differently (its own filter and
 * formulas, spec 190 §2). Stated in every document's criteria line, so an exported copy
 * carries it as well as the screen, the way Exago's own printout carries its PPD note.
 */
export const REPORT_SCOPE_NOTES = Object.freeze([
  'Excludes unallocated payments (PPD) and credits',
  'Excludes clients with no Billing Attorney',
  'Last payment and Paid in Last 30 Days are as of today',
])

/**
 * The run's criteria, as every Report Center document states them. `scope` adds
 * REPORT_SCOPE_NOTES and `percents` Exago's share-of-balance line (documents only; the
 * screen states both in its own notes block and table).
 */
export const describeExagoFilters = ({ asOf, earliest }, fixed = [], { scope = false, percents = null } = {}) => describeFilters(
  dateRangePart(null, asOf),
  earliest && earliest !== EARLIEST_DEFAULT ? `Payments received from ${fmtDisplayDate(earliest)}` : null,
  ...fixed.map((p) => `${p.name}: ${p.value}`),
  ...(scope ? REPORT_SCOPE_NOTES : []),
  percents ? percentSummary(percents) : null,
)
